import { api } from './_client';
import { toUploadError } from './files/upload';
import type {
  DeleteObjectResponse,
  FileObjectResponse,
  FileObjectUploadResponse,
  ObjectDownloadResponse,
  SharedObjectListResponse,
} from '../../types/api';

/**
 * The registry-object read **and write** surface (#1205, then STD-029 B-3).
 *
 * A separate domain module from `objectShares.ts` (STD-004) because it mirrors a
 * separate backend router: these routes are `/api/file-objects`, not
 * `/api/object-shares`. The split is not bookkeeping — the two answer different
 * questions. `objectShares.ts` lists share ROWS (what was granted, to whom);
 * this lists the objects those rows make REACHABLE, which for a folder grant is
 * a set the share table cannot enumerate.
 *
 * Everything here is addressed by registry **id** — never an S3 key, never a
 * display path. A shared object lives outside the caller's own prefix, so a path
 * could not authorize it even in principle.
 */
export const fileObjectsApi = {
  /**
   * The objects that cross an organization boundary, in ONE direction.
   *
   * `direction` has no "both" mode, and that is the endpoint's contract rather
   * than an omission: inbound entries are reached objects and outbound entries
   * are grants, so a union would page a set whose rows do not mean the same
   * thing and whose `total` would answer neither question.
   */
  async listSharedObjects(params: {
    direction: 'inbound' | 'outbound';
    counterpartyOrganizationId?: string;
    limit?: number;
    offset?: number;
  }): Promise<SharedObjectListResponse> {
    const query = new URLSearchParams({ direction: params.direction });
    if (params.counterpartyOrganizationId) {
      query.append('counterparty_organization_id', params.counterpartyOrganizationId);
    }
    // Sent only when set, so the default page size stays the server's to choose
    // and the query key does not carry a number the caller never picked.
    if (params.limit != null) query.append('limit', String(params.limit));
    if (params.offset != null) query.append('offset', String(params.offset));

    const response = await api.get<SharedObjectListResponse>(
      `/api/file-objects/shared?${query.toString()}`
    );
    return response.data;
  },

  /**
   * Presign a download for one object the caller may read.
   *
   * Works for a file inside a *shared* folder, which `/api/files/{path}/download`
   * cannot: that endpoint asks "is this path inside the caller's own org
   * prefix?", a question no shared object can pass. This one asks the database
   * whether the caller may SELECT the row.
   *
   * A folder is refused with `FILE_OBJECT_IS_FOLDER` rather than 404 — callers
   * should not offer the control at all (see `canDownloadSharedObject`).
   */
  async getObjectDownloadUrl(objectId: string): Promise<ObjectDownloadResponse> {
    const response = await api.get<ObjectDownloadResponse>(
      `/api/file-objects/${objectId}/download`
    );
    return response.data;
  },

  /**
   * Create a folder inside the folder named by `parentId` (STD-029 R-11).
   *
   * `parentId: null` means the caller's OWN organization root — the one node no
   * listing names, because it is the prefix every display path is relative to.
   * It cannot address anyone else's root: the organization is read from the
   * caller's JWT server-side, never from this payload. Creating inside a folder
   * somebody SHARED still needs that folder's id, which is the only spelling a
   * share can authorize.
   *
   * Replaces `POST /api/files/create-folder`, which built the parent by walking
   * the caller's own tree from a path string and therefore could never reach a
   * shared folder. That endpoint still exists for MissionPlanner's folder
   * picker; nothing in this app may call it.
   */
  async createFolderObject(params: {
    parentId: string | null;
    name: string;
  }): Promise<FileObjectResponse> {
    const response = await api.post<FileObjectResponse>('/api/file-objects/folders', {
      parent_id: params.parentId,
      name: params.name,
    });
    return response.data;
  },

  /**
   * Rename and/or move one object.
   *
   * ONE call for both, because the database does both in one row UPDATE:
   * splitting a combined gesture would make a lost race visible as a
   * half-applied move (the file in the new folder under the old name).
   *
   * The bytes never move (M-2/M-7) — `blob_key` is immutable, so this is
   * metadata only and no share, flight link or analysis verdict is disturbed.
   * That is why the call takes no key and returns none.
   */
  async updateObject(
    objectId: string,
    changes: { name?: string; parentId?: string | null }
  ): Promise<FileObjectResponse> {
    const body: { name?: string; parent_id?: string | null } = {};
    // Sent only when the caller actually asked for it: the server refuses a
    // PATCH that names nothing (422), and a payload that always carried both
    // keys would turn "rename in place" into "move to wherever undefined
    // serialises to".
    if (changes.name !== undefined) body.name = changes.name;
    if (changes.parentId !== undefined) body.parent_id = changes.parentId;

    const response = await api.patch<FileObjectResponse>(`/api/file-objects/${objectId}`, body);
    return response.data;
  },

  /**
   * Soft-delete one object; `recursive` also takes a folder's contents.
   *
   * The row is tombstoned and the bytes are destroyed — see the endpoint's
   * docstring for why that asymmetry is deliberate. Owner-only, enforced by a
   * database trigger: a write share conveys rename and move, never destruction
   * (R-5), and a grantee gets a 404 rather than a 403.
   */
  async deleteObject(
    objectId: string,
    options: { recursive?: boolean } = {}
  ): Promise<DeleteObjectResponse> {
    const query = options.recursive ? '?recursive=true' : '';
    const response = await api.delete<DeleteObjectResponse>(
      `/api/file-objects/${objectId}${query}`
    );
    return response.data;
  },

  /**
   * Upload one file INTO the folder named by `parentId` (#1262, then #1351).
   *
   * The only upload route that reaches a folder another organization shared —
   * `POST /api/files/upload` takes a `destination_path`, and inside a share that
   * path names nothing in the caller's own tree. Authorized by RLS alone: a
   * `write` share admits it, a read-only share answers 403, an invisible folder
   * 404. The row belongs to the folder's OWNER, which for a grantee is not the
   * caller — the response says whose it became.
   *
   * Since #1351 a `.csv` runs the same flight ingest as `/api/files/upload`
   * (metadata, hours, weather, the duplicate guards, a `fleet.flights` row,
   * analysis), so the attribution fields ride along exactly as they do there.
   * A flight log into a folder ANOTHER organization owns is refused with
   * `FILE_OBJECT_FLIGHT_LOG_FOREIGN_FOLDER` (422) before anything is written:
   * that flight would be theirs and only they can create it. Documents land.
   *
   * Errors are thrown in the same shape as `uploadFile`'s — `errorCode` carried
   * on the Error — so the batch uploader classifies both routes by one rule.
   */
  async uploadIntoObject(
    parentId: string,
    file: File,
    metadata: {
      flightType: string;
      payloadId: number;
      pilotId: string;
      customerId?: string;
      projectId?: string;
      airspaceObserverId?: string;
      payloadOperatorId?: string;
    }
  ): Promise<FileObjectUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.flightType) formData.append('flight_type', metadata.flightType);
    if (metadata.payloadId) formData.append('payload_id', metadata.payloadId.toString());
    if (metadata.pilotId) formData.append('pilot_id', metadata.pilotId);
    if (metadata.customerId) formData.append('customer_id', metadata.customerId);
    if (metadata.projectId) formData.append('project_id', metadata.projectId);
    if (metadata.airspaceObserverId) {
      formData.append('airspace_observer_id', metadata.airspaceObserverId);
    }
    if (metadata.payloadOperatorId) {
      formData.append('payload_operator_id', metadata.payloadOperatorId);
    }

    try {
      const response = await api.post<FileObjectUploadResponse>(
        `/api/file-objects/${parentId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 minutes for large file uploads
        }
      );
      return response.data;
    } catch (err: unknown) {
      throw toUploadError(err);
    }
  },
};
